import { type FC, lazy, Suspense } from "react"
import { useTheme } from "styled-components"

import { toCountFormat, toNumberFormat } from "@forento/core/utilities/number"
import AspectRatio from "@forento/ui/components/AspectRatio"

import PartialLoadingPage from "~/components/PartialLoadingPage"

import ChartCategorySelector, { type ChartCategory } from "./ChartCategorySelector"
import { AnalyticsCard } from "./shared"

const ResponsiveLine = lazy(() => import("@nivo/line").then(module => ({ default: module.ResponsiveLine })))

type Props = {
	label: string
	unit?: string
	values: { name: string; value: number }[]
	periodTitle: string
	selectedChartCategory: ChartCategory
	setSelectedChartCategory(newValue: ChartCategory): void
}
const Chart: FC<Props> = ({ label, unit, values, periodTitle, selectedChartCategory, setSelectedChartCategory }) => {
	const { primaryColor } = useTheme()
	const data = [{ id: label, data: values.map(value => ({ x: value.name, y: value.value })) }]
	const total = values.reduce((sum, current) => sum + current.value, 0)
	const xAxisTickValues = getXAxisTickValues(values.map(value => value.name))

	return (
		<AnalyticsCard gridArea="chart" className="min-w-0">
			<div className="mb-5 flex flex-wrap items-start justify-between gap-4">
				<ChartCategorySelector selectedValue={selectedChartCategory} onChange={setSelectedChartCategory} />
				{values.length > 0 && (
					<div className="flex flex-col items-end">
						<div className="mb-2 flex items-center text-sm text-gray-600">
							<div className="bg-primary mr-2 h-1.5 w-5 rounded-md" />
							{periodTitle}
						</div>
						<div className="font-bold">
							{toNumberFormat(roundChartValue(total))}
							{unit !== undefined ? ` ${unit}` : ""}
						</div>
					</div>
				)}
			</div>
			<AspectRatio aspectRatio={3} width={{ unit: "percent", value: 100 }}>
				<Suspense fallback={<PartialLoadingPage />}>
					<ResponsiveLine
						data={data}
						margin={{ top: 10, right: 10, bottom: 34, left: 36 }}
						colors={[primaryColor]}
						curve="monotoneX"
						enableArea={false}
						enableGridX={false}
						enablePoints={false}
						useMesh
						yFormat={value => toNumberFormat(roundChartValue(Number(value)))}
						axisBottom={{ tickPadding: 10, tickValues: xAxisTickValues }}
						axisLeft={{ tickPadding: 10, format: value => toCountFormat(Number(value)) }}
						tooltip={({ point }) => (
							<div className="min-w-48 rounded-lg border border-[#e8e8e8] bg-white px-3 py-2.5 text-black shadow-lg">
								<p className="mb-2 text-xs text-[#666666]">{point.data.xFormatted}</p>
								<div className="flex items-center gap-2 whitespace-nowrap">
									<div className="bg-primary size-2 shrink-0 rounded-full" />
									<p className="min-w-0 flex-1 text-sm capitalize">{label}</p>
									<p className="shrink-0 text-sm font-bold">
										{toNumberFormat(roundChartValue(Number(point.data.y)))}
										{unit !== undefined ? ` ${unit}` : ""}
									</p>
								</div>
							</div>
						)}
						theme={{
							axis: { ticks: { text: { fill: "#000000", fontSize: 12 } } },
							grid: { line: { stroke: "#EBEBEB" } },
						}}
					/>
				</Suspense>
			</AspectRatio>
		</AnalyticsCard>
	)
}

function roundChartValue(value: number): number {
	return Math.round(value * 100) / 100
}

function getXAxisTickValues(values: string[]): string[] {
	const maxTicks = 8
	if (values.length <= maxTicks) return values

	return [
		...new Set(
			Array.from({ length: maxTicks }, (_, index) => {
				const valueIndex = Math.round((index * (values.length - 1)) / (maxTicks - 1))
				return values[valueIndex]
			}),
		),
	]
}

export default Chart
